import React from 'react';
import { graphql } from 'gatsby';
import Accordion from './Accordion';
import ContainedImage from './ContainedImage';
import Content from './Content';
import ExternalVideo from './ExternalVideo';
import FeaturedProducts from './FeaturedProducts';
import FullWidthImage from './FullWidthImage';
import IconContentBlocks from './IconContentBlocks';
import ImageContent from './ImageContent';
import ImagesGrid from './ImagesGrid';
import InternalVideo from './InternalVideo';
import LinkCards from './LinkCards';
import Table from './Table';
import Tabs from './Tabs';
import Testimonials from './Testimonials';
import Timeline from './Timeline';

const ModularBlocks = ({ items, isBlogSingle }) =>
  items &&
  items.length > 0 &&
  items.map(item => (
    <React.Fragment key={item.id}>
      {item.model.apiKey === 'accordion_modular_block' && (
        <Accordion
          heading={item.instance.heading}
          items={item.instance.accordionItems}
        />
      )}
      {item.model.apiKey === 'contained_image_modular_block' && (
        <ContainedImage image={item.image} />
      )}
      {item.model.apiKey === 'content_modular_block' && (
        <Content
          html={item.content}
          contain={item.contain}
          twoColumns={item.twoColumns}
          highlight={item.highlight}
          isBlogSingle={isBlogSingle}
        />
      )}
      {item.model.apiKey === 'external_video_modular_block' && (
        <ExternalVideo video={item.externalVideo} />
      )}
      {item.model.apiKey === 'featured_products_modular_block' && (
        <FeaturedProducts
          heading={item.heading}
          items={item.items}
          link={item.link}
        />
      )}
      {item.model.apiKey === 'full_width_image_modular_block' && (
        <FullWidthImage image={item.image} />
      )}
      {item.model.apiKey === 'icon_content_blocks_modular_block' && (
        <IconContentBlocks
          heading={item.heading}
          items={item.items}
          textOnMobile={item.textOnMobile}
          altBackground={item.altBackground}
        />
      )}
      {item.model.apiKey === 'image_content_modular_block_v1' && (
        <ImageContent
          image={item.image}
          heading={item.heading}
          text={item.text}
          link={item.link}
          flip={item.flip}
          version={1}
        />
      )}
      {item.model.apiKey === 'image_content_modular_block_v2' && (
        <ImageContent
          image={item.image}
          heading={item.heading}
          text={item.text}
          link={item.link}
          flip={item.flip}
          version={2}
        />
      )}
      {item.model.apiKey === 'images_grid_modular_block' && (
        <ImagesGrid images={item.images} />
      )}
      {item.model.apiKey === 'internal_video_modular_block' && (
        <InternalVideo video={item.internalVideo} />
      )}
      {item.model.apiKey === 'link_cards_modular_block' && (
        <LinkCards items={item.items} />
      )}
      {item.model.apiKey === 'table_modular_block' && (
        <Table html={item.table} isBlogSingle={isBlogSingle} />
      )}
      {item.model.apiKey === 'tabs_modular_block' && (
        <Tabs heading={item.instance.heading} items={item.instance.tabs} />
      )}
      {item.model.apiKey === 'testimonials_modular_block' && (
        <Testimonials items={item.items} />
      )}
      {item.model.apiKey === 'timeline_modular_block' && (
        <Timeline events={item.events} />
      )}
    </React.Fragment>
  ));

export default ModularBlocks;

export const ModularBlockFragments = graphql`
  fragment AccordionModularBlockFragment on DatoCmsAccordionModularBlock {
    id
    model {
      apiKey
    }
    instance {
      heading
      accordionItems {
        id
        heading
        content
      }
    }
  }

  fragment ContainedImageModularBlockFragment on DatoCmsContainedImageModularBlock {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 1120)
      alt
    }
  }

  fragment ContentModularBlockFragment on DatoCmsContentModularBlock {
    id
    model {
      apiKey
    }
    content
    twoColumns
    highlight
    contain
  }

  fragment ExternalVideoModularBlockFragment on DatoCmsExternalVideoModularBlock {
    id
    model {
      apiKey
    }
    externalVideo {
      height
      width
      provider
      providerUid
      url
    }
  }

  fragment FeaturedProductsModularBlockFragment on DatoCmsFeaturedProductsModularBlock {
    id
    model {
      apiKey
    }
    heading
    products {
      shopifyProduct
    }
    link {
      text
      page {
        ...LinkFragment
      }
    }
  }

  fragment FullWidthImageModularBlockFragment on DatoCmsFullWidthImageModularBlock {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 2000, layout: FULL_WIDTH)
      alt
    }
  }

  fragment IconContentBlocksModularBlockFragment on DatoCmsIconContentBlocksModularBlock {
    id
    model {
      apiKey
    }
    heading
    items {
      ...IconContentBlocksFragment
    }
    textOnMobile
    altBackground
  }

  fragment ImageContentModularBlockV1Fragment on DatoCmsImageContentModularBlockV1 {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 710, height: 520)
      alt
    }
    heading
    text
    link {
      text
      page {
        ...LinkFragment
      }
    }
    flip
  }

  fragment ImageContentModularBlockV2Fragment on DatoCmsImageContentModularBlockV2 {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 900, height: 800)
      alt
    }
    heading
    text
    link {
      text
      page {
        ...LinkFragment
      }
    }
    flip
  }

  fragment ImagesGridModularBlockFragment on DatoCmsImagesGridModularBlock {
    id
    model {
      apiKey
    }
    images {
      gatsbyImageData(width: 1000, height: 500)
      alt
    }
  }

  fragment InternalVideoModularBlockFragment on DatoCmsInternalVideoModularBlock {
    id
    model {
      apiKey
    }
    internalVideo {
      format
      url
      video {
        thumbnailUrl
        mp4Url
      }
    }
  }

  fragment LinkCardsModularBlockFragment on DatoCmsLinkCardsModularBlock {
    id
    model {
      apiKey
    }
    items {
      ...LinkCardsFragment
    }
  }

  fragment TableModularBlockFragment on DatoCmsTableModularBlock {
    id
    model {
      apiKey
    }
    table
  }

  fragment TabsModularBlockFragment on DatoCmsTabsModularBlock {
    id
    model {
      apiKey
    }
    instance {
      heading
      tabs {
        id
        heading
        content
      }
    }
  }

  fragment TestimonialsModularBlockFragment on DatoCmsTestimonialsModularBlock {
    id
    model {
      apiKey
    }
    items {
      text
      logo {
        alt
        url
      }
    }
  }

  fragment TimelineModularBlockFragment on DatoCmsTimelineModularBlock {
    id
    model {
      apiKey
    }
    events {
      id
      image {
        gatsbyImageData(width: 710, height: 520)
        alt
      }
      year
      heading
      text
    }
  }
`;
